const MONITOR_SESSION_KEY = "naru_frontend_next_monitor";
const DEVICE_KEYS_KEY = "naru_frontend_next_recent_devices";

function apiBasePath() {
  const { port } = window.location;
  if (port === "3000") {
    return "/api/v1";
  }
  return "/next-api";
}

export function getStoredMonitorSession() {
  const raw = localStorage.getItem(MONITOR_SESSION_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (_error) {
    localStorage.removeItem(MONITOR_SESSION_KEY);
    return null;
  }
}

export function storeMonitorSession(session) {
  localStorage.setItem(MONITOR_SESSION_KEY, JSON.stringify(session));
}

export function clearMonitorSession() {
  localStorage.removeItem(MONITOR_SESSION_KEY);
}

export function getRecentDeviceKeys() {
  const raw = localStorage.getItem(DEVICE_KEYS_KEY);
  if (!raw) {
    return [];
  }
  try {
    const values = JSON.parse(raw);
    return Array.isArray(values) ? values.filter(Boolean) : [];
  } catch (_error) {
    localStorage.removeItem(DEVICE_KEYS_KEY);
    return [];
  }
}

export function saveRecentDeviceKey(deviceKey) {
  const clean = (deviceKey || "").trim();
  if (!clean) {
    return;
  }
  const next = [clean, ...getRecentDeviceKeys().filter((value) => value !== clean)].slice(0, 8);
  localStorage.setItem(DEVICE_KEYS_KEY, JSON.stringify(next));
}

async function apiFetch(path, init = {}) {
  const response = await fetch(`${apiBasePath()}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const payload = await response.json();
      detail = payload.detail || payload.message || detail;
    } catch (_error) {
      // ignore
    }
    if (response.status === 401) {
      clearMonitorSession();
    }
    throw new Error(detail);
  }

  return response.json();
}

export async function monitorLogin(username, password) {
  const data = await apiFetch("/monitor/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
  storeMonitorSession(data.monitor);
  return data.monitor;
}

export async function monitorMe() {
  const data = await apiFetch("/monitor/me", { method: "GET" });
  storeMonitorSession(data.monitor);
  return data.monitor;
}

export async function monitorLogout() {
  try {
    await apiFetch("/monitor/logout", { method: "POST" });
  } finally {
    clearMonitorSession();
  }
}

export async function fetchLatestSensorValues(deviceKey) {
  return apiFetch(`/monitor/devices/${encodeURIComponent(deviceKey)}/sensors/latest`, {
    method: "GET",
  });
}

export async function fetchSensorEvents(deviceKey, limit = 20) {
  const params = new URLSearchParams({ limit: String(limit) });
  return apiFetch(`/monitor/devices/${encodeURIComponent(deviceKey)}/sensor-events?${params.toString()}`, {
    method: "GET",
  });
}

export async function fetchMonitorGroups() {
  return apiFetch("/monitor/groups", { method: "GET" });
}

export async function fetchMonitorMainCards() {
  return apiFetch("/monitor/main-cards", { method: "GET" });
}

export async function fetchMonitorAlerts(statusFilter = "active", limit = 20) {
  const params = new URLSearchParams({
    status_filter: statusFilter,
    limit: String(limit),
  });
  return apiFetch(`/monitor/alerts?${params.toString()}`, { method: "GET" });
}

export async function acknowledgeMonitorAlert(alertId) {
  return apiFetch(`/monitor/alerts/${alertId}/acknowledge`, { method: "POST" });
}

export async function fetchMonitorGroupDevices(groupCode) {
  return apiFetch(`/monitor/groups/${encodeURIComponent(groupCode)}/devices`, { method: "GET" });
}

export async function fetchMonitorDevices() {
  return apiFetch("/monitor/devices", { method: "GET" });
}

export async function fetchMonitorDeviceOverview(deviceKey, category = "") {
  const params = new URLSearchParams();
  if (category) {
    params.set("category", category);
  }
  const query = params.toString();
  return apiFetch(`/monitor/devices/${encodeURIComponent(deviceKey)}/overview${query ? `?${query}` : ""}`, {
    method: "GET",
  });
}

export async function fetchMonitorItemDetail(deviceKey, itemId) {
  return apiFetch(`/monitor/devices/${encodeURIComponent(deviceKey)}/items/${itemId}`, {
    method: "GET",
  });
}

export async function sendMonitorControl(deviceKey, itemId, controlIndex, buttonIndex = null) {
  const basePath = `/monitor/devices/${encodeURIComponent(deviceKey)}/items/${itemId}/control/${controlIndex}`;
  const path = buttonIndex === null ? basePath : `${basePath}/button/${buttonIndex}`;
  return apiFetch(path, { method: "POST" });
}
